import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// components
import Header from 'components/UI/Modal/Header'
import DrawerModal from 'components/UI/Drawer'
import SettingsForm from './SettingsForm'
// hooks
import { useModalData } from 'hooks/index'
// constants
import { Modals } from 'constants/modals'
import { ENVIRONMENT_OPTIONS } from './constants'
// graphql
import { INJURY_MASTER_SETTINGS_QUERY } from 'graphql/injuryMaster/queries'
// types
import { IInjuryMasterSettings } from 'types/graphql'
// views
import { Wrapper } from './views'

interface ModalData {
  isOpen: boolean
  settings?: IInjuryMasterSettings
}

const InjuryMasterSettingsModal: React.FC = () => {
  const {
    hideModal,
    modalData: { isOpen, settings: modalSettings }
  } = useModalData<ModalData>({ modalType: Modals.INJURY_MASTER_SETTINGS_MODAL })

  // The live query, not the snapshot passed when the modal opened: after a first save the drawer
  // stays open so a company can be chosen, and that needs to see the saved connection.
  const { data } = useQuery<{ injuryMasterSettings: IInjuryMasterSettings }>(INJURY_MASTER_SETTINGS_QUERY, {
    skip: !isOpen
  })

  const settings = data?.injuryMasterSettings ?? modalSettings

  const initialValues = React.useMemo(
    () => ({
      accountId: settings?.accountId || '',
      bearerToken: '',
      environment: ENVIRONMENT_OPTIONS.find(({ value }) => value === settings?.environment),
      // Only the id is stored; the form swaps in the company name once the list has loaded.
      companyId: settings?.companyId ? { value: settings.companyId, label: settings.companyId } : undefined,
      // Only the id is stored; the form swaps in the scheme name once the list has loaded.
      schemeId: settings?.schemeId ? { value: String(settings.schemeId), label: String(settings.schemeId) } : undefined
    }),
    [settings]
  )

  return (
    <DrawerModal isOpen={isOpen} width='560px' hideModal={hideModal}>
      <Header handleClearClick={hideModal}>{settings ? 'Update Injury Master' : 'Connect Injury Master'}</Header>
      <Wrapper>
        {isOpen && <SettingsForm hideModal={hideModal} isConnected={!!settings} initialValues={initialValues} />}
      </Wrapper>
    </DrawerModal>
  )
}

export default InjuryMasterSettingsModal
